import { useRef, useState } from 'react';
import { api, photoUrl, type Lot } from '../api';
import { ErrorBox, Modal } from './ui';

/** Shrinks big phone photos before upload (saves mobile data). Falls back to the original file. */
async function prepare(file: File): Promise<Blob> {
  if (file.size < 1_500_000 || !('createImageBitmap' in window)) return file;
  try {
    const bmp = await createImageBitmap(file);
    const scale = Math.min(1, 2560 / Math.max(bmp.width, bmp.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bmp.width * scale);
    canvas.height = Math.round(bmp.height * scale);
    canvas.getContext('2d')!.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/jpeg', 0.9));
    return blob ?? file;
  } catch {
    return file;
  }
}

export async function uploadPhoto(file: File, lotId?: string): Promise<{ id: string }> {
  const form = new FormData();
  if (lotId) form.append('lotId', lotId);
  form.append('photo', await prepare(file), file.name || 'photo.jpg');
  const r = await api<{ photo: { id: string } }>('/photos', { form, method: 'POST' });
  return r.photo;
}

function PhotoInputs({ onFiles, disabled }: { onFiles: (files: File[]) => void; disabled?: boolean }) {
  const cam = useRef<HTMLInputElement>(null);
  const lib = useRef<HTMLInputElement>(null);
  const handle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = '';
    if (files.length) onFiles(files);
  };
  return (
    <div className="row gap">
      <button type="button" className="btn" disabled={disabled} onClick={() => cam.current?.click()}>
        📷 Take photo
      </button>
      <button type="button" className="btn btn-secondary" disabled={disabled} onClick={() => lib.current?.click()}>
        🖼️ Upload files
      </button>
      <input ref={cam} type="file" accept="image/*" capture="environment" hidden onChange={handle} />
      <input ref={lib} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={handle} />
    </div>
  );
}

export interface PendingPhoto {
  key: string;
  id?: string; // server public id once uploaded
  preview?: string; // local object URL while uploading
  status: 'uploading' | 'done' | 'error';
  error?: string;
  file?: File;
}

/** Photos taken BEFORE the lot is saved. Each uploads immediately; the lot save attaches them. */
export function PendingPhotos({
  photos,
  setPhotos,
  primary,
  setPrimary,
}: {
  photos: PendingPhoto[];
  setPhotos: (fn: (p: PendingPhoto[]) => PendingPhoto[]) => void;
  primary: string | null;
  setPrimary: (id: string | null) => void;
}) {
  const start = (p: PendingPhoto) => {
    setPhotos((all) => all.map((x) => (x.key === p.key ? { ...x, status: 'uploading', error: undefined } : x)));
    uploadPhoto(p.file!)
      .then((r) => setPhotos((all) => all.map((x) => (x.key === p.key ? { ...x, id: r.id, status: 'done', file: undefined } : x))))
      .catch((e) => setPhotos((all) => all.map((x) => (x.key === p.key ? { ...x, status: 'error', error: e.message } : x))));
  };
  const add = (files: File[]) => {
    const items: PendingPhoto[] = files.map((f) => ({ key: crypto.randomUUID(), file: f, preview: URL.createObjectURL(f), status: 'uploading' }));
    setPhotos((all) => [...all, ...items]);
    items.forEach(start);
  };
  const move = (i: number, d: -1 | 1) =>
    setPhotos((all) => {
      const next = [...all];
      const j = i + d;
      if (j < 0 || j >= next.length) return all;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  const remove = (p: PendingPhoto) => {
    if (p.id) api(`/photos/pending/${p.id}`, { method: 'DELETE' }).catch(() => undefined);
    if (p.id && primary === p.id) setPrimary(null);
    setPhotos((all) => all.filter((x) => x.key !== p.key));
  };
  const effectivePrimary = primary ?? photos.find((p) => p.id)?.id ?? null;

  return (
    <div>
      <PhotoInputs onFiles={add} />
      {photos.length > 0 && (
        <div className="photo-grid">
          {photos.map((p, i) => (
            <div key={p.key} className={`photo-cell ${p.id && p.id === effectivePrimary ? 'is-primary' : ''}`}>
              <img src={p.id ? photoUrl(p.id) : p.preview} alt="" />
              {p.status === 'uploading' && <div className="photo-overlay">Uploading…</div>}
              {p.status === 'error' && (
                <div className="photo-overlay photo-error">
                  Failed
                  <button type="button" className="btn btn-small" onClick={() => start(p)}>
                    Retry
                  </button>
                </div>
              )}
              <div className="photo-actions">
                <button type="button" title="Move left" onClick={() => move(i, -1)} disabled={i === 0}>
                  ◀
                </button>
                <button type="button" title="Make primary" disabled={!p.id} onClick={() => setPrimary(p.id!)}>
                  {p.id && p.id === effectivePrimary ? '★' : '☆'}
                </button>
                <button type="button" title="Remove" onClick={() => remove(p)}>
                  🗑
                </button>
                <button type="button" title="Move right" onClick={() => move(i, 1)} disabled={i === photos.length - 1}>
                  ▶
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Photos of a saved lot. Every change is saved immediately and the lot is reloaded from the server. */
export function LotPhotos({ lot, onChange, readOnly }: { lot: Lot; onChange: (l: Lot) => void; readOnly?: boolean }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [viewing, setViewing] = useState<string | null>(null);

  const run = async (fn: () => Promise<{ lot: Lot } | void>) => {
    setBusy(true);
    setError(null);
    try {
      const r = await fn();
      if (r) onChange(r.lot);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const ids = lot.photos.map((p) => p.id);
  const move = (i: number, d: -1 | 1) => {
    const next = [...ids];
    const j = i + d;
    [next[i], next[j]] = [next[j], next[i]];
    run(() => api(`/lots/${lot.id}/photos/order`, { body: { photoIds: next } }));
  };

  return (
    <div>
      <ErrorBox error={error} />
      {!readOnly && (
        <PhotoInputs
          disabled={busy}
          onFiles={(files) =>
            run(async () => {
              for (const f of files) await uploadPhoto(f, lot.id);
              return api(`/lots/${lot.id}`);
            })
          }
        />
      )}
      {busy && <div className="muted">Saving…</div>}
      {lot.photos.length === 0 ? (
        <p className="missing">No photos yet.</p>
      ) : (
        <div className="photo-grid">
          {lot.photos.map((p, i) => (
            <div key={p.id} className={`photo-cell ${p.isPrimary ? 'is-primary' : ''}`}>
              {p.source === 'catalogue' && <span className="photo-badge">Costco</span>}
              <img src={photoUrl(p.id)} alt="" onClick={() => setViewing(p.id)} />
              {!readOnly && (
                <div className="photo-actions">
                  <button type="button" title="Move left" disabled={busy || i === 0} onClick={() => move(i, -1)}>
                    ◀
                  </button>
                  <button type="button" title="Make primary" disabled={busy || p.isPrimary} onClick={() => run(() => api(`/lots/${lot.id}/photos/${p.id}/primary`, { method: 'POST' }))}>
                    {p.isPrimary ? '★' : '☆'}
                  </button>
                  <button
                    type="button"
                    title="Remove"
                    disabled={busy}
                    onClick={() => confirm('Remove this photo from the lot?') && run(() => api(`/lots/${lot.id}/photos/${p.id}`, { method: 'DELETE' }))}
                  >
                    🗑
                  </button>
                  <button type="button" title="Move right" disabled={busy || i === lot.photos.length - 1} onClick={() => move(i, 1)}>
                    ▶
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {viewing && (
        <Modal title="Photo" onClose={() => setViewing(null)}>
          <img src={photoUrl(viewing, 'full')} alt="" className="photo-full" />
        </Modal>
      )}
    </div>
  );
}
